'use client';

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Extension } from '@tiptap/core';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  ChevronDown,
  Code,
  Eraser,
  Eye,
  EyeOff,
  Heading2,
  Highlighter,
  Image as ImageIcon,
  Indent,
  Italic,
  Link2,
  List,
  ListOrdered,
  ListTodo,
  Minus,
  Outdent,
  Quote,
  Redo2,
  Strikethrough,
  Table as TableIcon,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';
import { toWizardEditorHtml, WIZARD_FONT_SIZES } from '@/components/event-wizard/wizard-rich-text-utils';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (size: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
  }
}

const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeightClass?: string;
  /** Smaller editor chrome for dense wizard steps. */
  compact?: boolean;
};

type MenuKey = 'formatting' | 'insert' | 'advanced' | null;

function toolbarBtn(active: boolean, disabled?: boolean) {
  return [
    'inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
    disabled ? 'cursor-not-allowed opacity-40' : '',
    active ? 'bg-soot/10 text-soot shadow-sm ring-1 ring-soot/10' : 'text-moss/75 hover:bg-bg/80 hover:text-soot',
  ]
    .filter(Boolean)
    .join(' ');
}

const menuBtn =
  'inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-xs font-semibold text-moss/80 transition hover:bg-bg/80 hover:text-soot disabled:cursor-not-allowed disabled:opacity-40';

const sep = 'mx-0.5 h-5 w-px bg-neutral-200/80';

export function WizardRichTextField({
  value,
  onChange,
  placeholder = 'Start typing…',
  disabled = false,
  minHeightClass = 'min-h-[100px]',
  compact = false,
}: Props) {
  const fontSizeId = useId();
  const lastHtmlRef = useRef(value);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<MenuKey>(null);
  const [preview, setPreview] = useState(false);
  const [, setTick] = useState(0);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: {
          HTMLAttributes: { class: 'wizard-bullet-list' },
        },
        orderedList: {
          HTMLAttributes: { class: 'wizard-ordered-list' },
        },
        listItem: {
          HTMLAttributes: { class: 'wizard-list-item' },
        },
        codeBlock: { HTMLAttributes: { class: 'wizard-editor-code' } },
        // TipTap v3 StarterKit bundles link + underline — disable to avoid duplicates.
        link: false,
        underline: false,
      }),
      Underline,
      TextStyle,
      FontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'wizard-editor-link' },
        autolink: true,
      }),
      Image.configure({ allowBase64: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      TaskList.configure({
        HTMLAttributes: { class: 'wizard-task-list' },
      }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: { class: 'wizard-task-item' },
      }),
      Placeholder.configure({ placeholder }),
    ],
    [placeholder],
  );

  const editor = useEditor({
    immediatelyRender: false,
    editable: !disabled,
    extensions,
    content: toWizardEditorHtml(value),
    editorProps: {
      attributes: {
        class: `wizard-rich-text-editor max-w-none focus:outline-none ${minHeightClass} px-3 py-2.5 text-sm text-soot`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      lastHtmlRef.current = html;
      onChange(html);
      setTick((n) => n + 1);
    },
    onSelectionUpdate: () => setTick((n) => n + 1),
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled && !preview);
  }, [editor, disabled, preview]);

  useEffect(() => {
    if (!editor) return;
    if (value === lastHtmlRef.current) return;
    lastHtmlRef.current = value;
    editor.commands.setContent(toWizardEditorHtml(value), { emitUpdate: false });
  }, [value, editor]);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!toolbarRef.current?.contains(event.target as Node)) setMenu(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const insertLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Enter link URL', previous || 'https://');
    if (url === null) return;
    const trimmed = url.trim();
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
    setMenu(null);
  }, [editor]);

  const insertImage = useCallback(
    (file: File) => {
      if (!editor) return;
      const reader = new FileReader();
      reader.onload = () => {
        const src = String(reader.result || '');
        if (src) editor.chain().focus().setImage({ src }).run();
      };
      reader.readAsDataURL(file);
      setMenu(null);
    },
    [editor],
  );

  const currentFontSize =
    (editor?.getAttributes('textStyle')?.fontSize as string | undefined) ?? '15px';

  return (
    <div
      className={`wizard-rich-text-field overflow-hidden rounded-xl border border-neutral-200/80 bg-bg/55 shadow-sm transition focus-within:border-nature-accent/40 focus-within:ring-2 focus-within:ring-nature-accent/15 ${
        compact ? 'wizard-rich-text-field--compact' : ''
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <div
        ref={toolbarRef}
        className={`relative z-20 flex flex-wrap items-center gap-0.5 border-b border-neutral-200/50 bg-bg/35 backdrop-blur-sm ${
          compact ? 'px-1.5 py-1' : 'px-2 py-1.5'
        }`}
      >
          <button
            type="button"
            className={toolbarBtn(false, disabled || !editor?.can().undo())}
            title="Undo"
            aria-label="Undo"
            disabled={disabled || !editor?.can().undo()}
            onClick={() => editor?.chain().focus().undo().run()}
          >
            <Undo2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className={toolbarBtn(false, disabled || !editor?.can().redo())}
            title="Redo"
            aria-label="Redo"
            disabled={disabled || !editor?.can().redo()}
            onClick={() => editor?.chain().focus().redo().run()}
          >
            <Redo2 className="h-3.5 w-3.5" />
          </button>

          <span className={sep} aria-hidden />

          <button
            type="button"
            className={toolbarBtn(Boolean(editor?.isActive('bold')), disabled)}
            title="Bold"
            aria-label="Bold"
            disabled={disabled || !editor}
            onClick={() => editor?.chain().focus().toggleBold().run()}
          >
            <Bold className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
          <button
            type="button"
            className={toolbarBtn(Boolean(editor?.isActive('italic')), disabled)}
            title="Italic"
            aria-label="Italic"
            disabled={disabled || !editor}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          >
            <Italic className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
          <button
            type="button"
            className={toolbarBtn(Boolean(editor?.isActive('underline')), disabled)}
            title="Underline"
            aria-label="Underline"
            disabled={disabled || !editor}
            onClick={() => editor?.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className={toolbarBtn(Boolean(editor?.isActive('heading', { level: 2 })), disabled)}
            title="Heading"
            aria-label="Heading"
            disabled={disabled || !editor}
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <Heading2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className={toolbarBtn(Boolean(editor?.isActive('bulletList')), disabled)}
            title="Bullet list"
            aria-label="Bullet list"
            disabled={disabled || !editor}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
          >
            <List className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className={toolbarBtn(Boolean(editor?.isActive('orderedList')), disabled)}
            title="Numbered list"
            aria-label="Numbered list"
            disabled={disabled || !editor}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className={toolbarBtn(Boolean(editor?.isActive('link')), disabled)}
            title="Insert link"
            aria-label="Insert link"
            disabled={disabled || !editor}
            onClick={insertLink}
          >
            <Link2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className={toolbarBtn(Boolean(editor?.isActive('table')), disabled)}
            title="Table"
            aria-label="Table"
            disabled={disabled || !editor}
            onClick={() =>
              editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
            }
          >
            <TableIcon className="h-3.5 w-3.5" />
          </button>
          <label className={`${toolbarBtn(false, disabled)} cursor-pointer`} title="Image" aria-label="Image">
            <ImageIcon className="h-3.5 w-3.5" />
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={disabled || !editor}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) insertImage(file);
                e.target.value = '';
              }}
            />
          </label>
          <button
            type="button"
            className={toolbarBtn(preview, disabled)}
            title={preview ? 'Edit mode' : 'Preview'}
            aria-label={preview ? 'Edit mode' : 'Preview'}
            disabled={disabled || !editor}
            onClick={() => setPreview((v) => !v)}
          >
            {preview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>

          <span className={sep} aria-hidden />

          <label className="sr-only" htmlFor={fontSizeId}>
            Font size
          </label>
          <select
            id={fontSizeId}
            disabled={disabled || !editor}
            value={WIZARD_FONT_SIZES.some((s) => s.value === currentFontSize) ? currentFontSize : '15px'}
            onChange={(e) => {
              const size = e.target.value;
              if (size === '15px') {
                editor?.chain().focus().unsetFontSize().run();
              } else {
                editor?.chain().focus().setFontSize(size).run();
              }
            }}
            className="h-8 rounded-lg border-0 bg-transparent px-2 text-xs font-semibold text-moss/80 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {WIZARD_FONT_SIZES.map((size) => (
              <option key={size.value} value={size.value}>
                {size.label}
              </option>
            ))}
          </select>

          <div className="relative">
            <button
              type="button"
              className={menuBtn}
              disabled={disabled || !editor}
              onClick={() => setMenu((m) => (m === 'formatting' ? null : 'formatting'))}
            >
              Formatting <ChevronDown className="h-3 w-3 opacity-70" />
            </button>
            {menu === 'formatting' ? (
              <div className="absolute left-0 top-full z-30 mt-1 w-52 rounded-xl border border-neutral-200/80 bg-bg/95 p-1 shadow-lg backdrop-blur-md">
                <MenuItem
                  label="Strikethrough"
                  icon={<Strikethrough className="h-3.5 w-3.5" />}
                  onClick={() => {
                    editor?.chain().focus().toggleStrike().run();
                    setMenu(null);
                  }}
                />
                <MenuItem
                  label="Highlight"
                  icon={<Highlighter className="h-3.5 w-3.5" />}
                  onClick={() => {
                    editor?.chain().focus().toggleHighlight({ color: '#fef3c7' }).run();
                    setMenu(null);
                  }}
                />
                <MenuItem
                  label="Quote"
                  icon={<Quote className="h-3.5 w-3.5" />}
                  onClick={() => {
                    editor?.chain().focus().toggleBlockquote().run();
                    setMenu(null);
                  }}
                />
                <MenuItem
                  label="Code block"
                  icon={<Code className="h-3.5 w-3.5" />}
                  onClick={() => {
                    editor?.chain().focus().toggleCodeBlock().run();
                    setMenu(null);
                  }}
                />
                <MenuItem
                  label="Checklist"
                  icon={<ListTodo className="h-3.5 w-3.5" />}
                  onClick={() => {
                    editor?.chain().focus().toggleTaskList().run();
                    setMenu(null);
                  }}
                />
                <MenuItem
                  label="Clear formatting"
                  icon={<Eraser className="h-3.5 w-3.5" />}
                  onClick={() => {
                    editor?.chain().focus().unsetAllMarks().clearNodes().run();
                    setMenu(null);
                  }}
                />
                <div className="my-1 border-t border-neutral-200/70" />
                <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-medium text-soot/80 hover:bg-neutral-50">
                  <span className="inline-flex h-3.5 w-3.5 items-center justify-center overflow-hidden rounded-sm border border-neutral-300">
                    <input
                      type="color"
                      className="-m-1 h-6 w-6 cursor-pointer border-0"
                      defaultValue="#1f2937"
                      onChange={(e) => {
                        editor?.chain().focus().setColor(e.target.value).run();
                        setMenu(null);
                      }}
                    />
                  </span>
                  Text color
                </label>
              </div>
            ) : null}
          </div>

          <div className="relative">
            <button
              type="button"
              className={menuBtn}
              disabled={disabled || !editor}
              onClick={() => setMenu((m) => (m === 'insert' ? null : 'insert'))}
            >
              Insert <ChevronDown className="h-3 w-3 opacity-70" />
            </button>
            {menu === 'insert' ? (
              <div className="absolute left-0 top-full z-30 mt-1 w-44 rounded-xl border border-neutral-200/80 bg-bg/95 p-1 shadow-lg backdrop-blur-md">
                <MenuItem
                  label="Horizontal rule"
                  icon={<Minus className="h-3.5 w-3.5" />}
                  onClick={() => {
                    editor?.chain().focus().setHorizontalRule().run();
                    setMenu(null);
                  }}
                />
                <MenuItem
                  label="Heading 1"
                  onClick={() => {
                    editor?.chain().focus().toggleHeading({ level: 1 }).run();
                    setMenu(null);
                  }}
                />
                <MenuItem
                  label="Heading 3"
                  onClick={() => {
                    editor?.chain().focus().toggleHeading({ level: 3 }).run();
                    setMenu(null);
                  }}
                />
                <MenuItem
                  label="Link"
                  icon={<Link2 className="h-3.5 w-3.5" />}
                  onClick={insertLink}
                />
              </div>
            ) : null}
          </div>

          <div className="relative">
            <button
              type="button"
              className={menuBtn}
              disabled={disabled || !editor}
              onClick={() => setMenu((m) => (m === 'advanced' ? null : 'advanced'))}
            >
              Advanced <ChevronDown className="h-3 w-3 opacity-70" />
            </button>
            {menu === 'advanced' ? (
              <div className="absolute left-0 top-full z-30 mt-1 w-44 rounded-xl border border-neutral-200/80 bg-bg/95 p-1 shadow-lg backdrop-blur-md">
                <MenuItem
                  label="Align left"
                  icon={<AlignLeft className="h-3.5 w-3.5" />}
                  onClick={() => {
                    editor?.chain().focus().setTextAlign('left').run();
                    setMenu(null);
                  }}
                />
                <MenuItem
                  label="Align center"
                  icon={<AlignCenter className="h-3.5 w-3.5" />}
                  onClick={() => {
                    editor?.chain().focus().setTextAlign('center').run();
                    setMenu(null);
                  }}
                />
                <MenuItem
                  label="Align right"
                  icon={<AlignRight className="h-3.5 w-3.5" />}
                  onClick={() => {
                    editor?.chain().focus().setTextAlign('right').run();
                    setMenu(null);
                  }}
                />
                <MenuItem
                  label="Indent"
                  icon={<Indent className="h-3.5 w-3.5" />}
                  onClick={() => {
                    editor?.chain().focus().sinkListItem('listItem').run();
                    setMenu(null);
                  }}
                />
                <MenuItem
                  label="Outdent"
                  icon={<Outdent className="h-3.5 w-3.5" />}
                  onClick={() => {
                    editor?.chain().focus().liftListItem('listItem').run();
                    setMenu(null);
                  }}
                />
              </div>
            ) : null}
          </div>
      </div>

      <div className={`wizard-rich-text-editor-scroll bg-bg/45 overflow-y-auto ${minHeightClass}`}>
        {preview ? (
          <div
            className={`wizard-rich-text-preview max-w-none px-3 py-2.5 text-sm text-soot ${minHeightClass}`}
            dangerouslySetInnerHTML={{ __html: toWizardEditorHtml(value) }}
          />
        ) : editor ? (
          <EditorContent editor={editor} />
        ) : (
          <div className={`animate-pulse ${minHeightClass}`} />
        )}
      </div>
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  icon,
}: {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs font-medium text-soot/85 hover:bg-neutral-50"
    >
      {icon} {label}
    </button>
  );
}
